import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, message, Modal, Row, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import BusBrandInput from '@/components/FormItems/BusBrandInput';
import { clearAllBusModelsCache } from '@/hooks/data/masterDataHook';
import { CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'busModelsPage_Title';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'busModelsPage_BusModelName',
    dataIndex: 'modelName',
    ellipsis: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'busModelsPage_BusBrandName',
    dataIndex: 'busBrandName',
    ellipsis: true,
  },
  {
    title: 'busModelsPage_ModelPower',
    dataIndex: 'modelPower',
    ellipsis: true,
  },
  {
    title: 'busModelsPage_modelYear',
    dataIndex: 'modelYear',
    ellipsis: true,
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'modelName', label: 'busModelsPage_BusModelName' }];

const BusModelsPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canUpdate = useCanManageFleetTrip();

  const clearCache = () => {
    clearAllBusModelsCache();
  };
  const createEntity = values => {
    setLoading(true);
    apiSdk.BusModelService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.BusModelService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setIsNewForm(true);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/bus-models/get-bus-models"
          bordered
          order_by="modelName"
          order_by_asc={true}
          allowEdit={canUpdate}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          allowDelete={canUpdate}
          delete_url="/fleets/bus-models/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            visible={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={isNewForm ? t('busModelsPage_CreateBusModel') : t('busModelsPage_UpdateBusModel')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item label={t('busModelsPage_BusModelName')} name="modelName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('busModelsPage_BusBrandName')} name="busBrandId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <BusBrandInput />
                    </Form.Item>
                    <Form.Item label={t('busModelsPage_ModelPower')} name="modelPower" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('busModelsPage_modelYear')} name="modelYear" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default BusModelsPage;
